import { Box, Typography, useTheme, Chip, Tooltip } from '@mui/material';
import { SettingsOutlined } from '@mui/icons-material';
import SettingCard from './SettingCard';
import SettingRow from './SettingRow';
import WolfButton from '@components/ui/WolfButton';
import { useTranslation } from '@hooks/useTranslation';
import { UserPreferences } from '@models/user.model';

interface GeneralSettingsSectionProps {
  loginMethod: string;
  preferences: UserPreferences | null | undefined;
  permanentlyDisabled: boolean;
  isImpersonating?: boolean;
  onPasswordChange: () => void;
  onLogRocketChange: () => void;
  onWolfieToggle: () => void;
  onTutorialLaunch: () => void;
}

const GeneralSettingsSection: React.FC<GeneralSettingsSectionProps> = ({
  loginMethod,
  preferences,
  permanentlyDisabled,
  isImpersonating = false,
  onPasswordChange,
  onLogRocketChange,
  onWolfieToggle,
  onTutorialLaunch,
}) => {
  const theme = useTheme();
  const localeSettings = useTranslation('settings');
  const localeCommon = useTranslation('common');
  const localeLogRocket = useTranslation('logRocket');
  const localeActionMessages = useTranslation('actionMessages');

  return (
    <SettingCard
      title={localeSettings['generalSettings'] || localeSettings['configuration']}
      icon={<SettingsOutlined />}
      iconColor={theme.palette.frozenGrayShades7.main}>
      {/* Change Password - Only for Cognito users */}
      {loginMethod === 'cognito' && (
        <SettingRow
          label={localeSettings['changePassword'] + ':'}
          description={localeSettings['changePasswordDescription']}>
          <WolfButton color="primary" onClick={onPasswordChange}>
            {localeSettings['changePassword']}
          </WolfButton>
        </SettingRow>
      )}

      {/* LogRocket Session Recording Preference */}
      <SettingRow
        label={localeSettings['sessionRecording'] + ':'}
        description={
          <Box className="flex items-center gap-2 mt-1">
            <Typography variant="body14" className="text-gray-600">
              {localeSettings['sessionRecordingDescription']}
            </Typography>
            <Chip
              label={
                preferences?.allowLogRocketRecording === 'identified'
                  ? localeLogRocket['statusAccepted']
                  : localeLogRocket['statusRejected']
              }
              size="small"
              color={
                preferences?.allowLogRocketRecording === 'identified'
                  ? 'primary'
                  : 'default'
              }
              variant="outlined"
            />
          </Box>
        }>
        <Tooltip title={isImpersonating ? localeActionMessages['whileImpersonatingError'] : ''}>
          <span>
            <WolfButton color="primary" onClick={onLogRocketChange} disabled={isImpersonating}>
              {localeCommon['change']}
            </WolfButton>
          </span>
        </Tooltip>
      </SettingRow>

      {/* Service Status */}
      <SettingRow label={localeSettings['serviceStatus'] + ':'} description={localeSettings['serviceStatusDescription']}>
        <WolfButton
          color="primary"
          onClick={() => window.open(process.env.REACT_APP_MARKETING_URL + '/health', '_blank')}>
          {localeSettings['viewStatus'] || localeCommon['clickView']}
        </WolfButton>
      </SettingRow>

      {/* Wolfie Status */}
      <SettingRow
        label={localeSettings['wolfieStatus'] + ':'}
        description={
          <Box className="flex items-center gap-2 mt-1">
            <Typography variant="body14" className="text-gray-600">
              {localeSettings['wolfieStatusDescription']}
            </Typography>
            <Chip
              label={permanentlyDisabled ? localeCommon['disabled'] : localeCommon['enabled']}
              size="small"
              color={permanentlyDisabled ? 'default' : 'success'}
              variant="outlined"
            />
          </Box>
        }>
        <WolfButton color="primary" onClick={onWolfieToggle}>
          {permanentlyDisabled ? localeCommon['enable'] : localeCommon['disable']}
        </WolfButton>
      </SettingRow>

      {/* Launch Tutorial */}
      <SettingRow
        label={localeSettings['launchTutorial'] + ':'}
        description={localeSettings['launchTutorialDescription']}>
        <WolfButton color="primary" onClick={onTutorialLaunch}>
          {localeSettings['startTutorial']}
        </WolfButton>
      </SettingRow>
    </SettingCard>
  );
};

export default GeneralSettingsSection;
